/**
 * Wire types for the tenant-management surface of the `tenants` module.
 *
 * `GET tenants/` and `GET tenants/:id/` return ONE OF TWO different shapes
 * depending on the caller's role (serializers.ts:98-139, routes.ts:496-501):
 *   - AGENT / SUPERAGENT -> `EnhancedTenant` (`EnhancedTenantSerializer`)
 *   - LANDLORD           -> the plain `User` shape from src/types/user.ts
 * `isEnhancedTenant` narrows between them; there is no server-side hint beyond
 * the caller's own `useAuth().user.role`, so callers should check role AND (for
 * safety) shape.
 */
import type { User } from './user';

/**
 * `EnhancedTenantSerializer` — serializers.ts:105-139.
 *
 * `property_name`, `property_location`, `unit_number` fall back to the literal
 * string `'N/A'` (not null) when the tenant has no active lease — preserved
 * verbatim, not normalised to null. `last_payment_amount` is `0`, not null,
 * for a tenant who has never paid.
 */
export interface EnhancedTenant {
  id: number;
  full_name: string;
  email: string;
  phone: string;
  property_name: string;
  property_location: string;
  unit_number: string;
  /** A `LeaseStatus` value, or the literal `'NO_ACTIVE_LEASE'` sentinel. */
  lease_status: string;
  last_payment_date: string | null;
  last_payment_amount: number;
  current_balance: number;
}

export type TenantRow = EnhancedTenant | User;

export function isEnhancedTenant(row: TenantRow): row is EnhancedTenant {
  return 'full_name' in row;
}

/**
 * `tenantCreateSchema` on write (serializers.ts:191-198).
 *
 * ⚠ If `password` is omitted, the backend defaults it to the well-known
 * literal `'password123'` for every tenant created this way — a pre-existing
 * behaviour ported as-is, not a bug in this frontend. Surface it in the UI
 * rather than hiding it.
 */
export interface TenantCreateRequest {
  email: string;
  username: string;
  first_name?: string | undefined;
  last_name?: string | undefined;
  phone?: string | undefined;
  password?: string | undefined;
}

/** `tenantPatchSchema` — every field of `TenantCreateRequest`, optional. */
export type TenantPatchRequest = Partial<TenantCreateRequest>;

/** `serializeCreatedTenant` output (serializers.ts:206-222) — `password` is write-only. */
export interface CreatedTenant {
  id: number;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  phone: string;
}
